// Media library picker of the page editor: pick an image or audio file of
// the product's media library for a localized field (its localization key).
import React, { useMemo, useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import AudioPlayer from "aldoo-ra/AudioPlayer"
import { TextInput } from "../../../AdminShared/ui"
import { urljoin } from "./useProductEditor"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"

const fileName = (url) => {
  const name = String(url).split("/").pop()
  // media files are stored under a base64 name: show the decoded original when possible
  const base = name.replace(/\.[^.]+$/, "")
  try {
    const decoded = decodeURIComponent(escape(atob(base.replace(/-/g, "/"))))
    return /^[\x20-\x7eЀ-ӿ]+$/.test(decoded) ? `${decoded}${name.slice(base.length)}` : name
  } catch (_) {
    return name
  }
}

export const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return ""
  const s = Math.round(seconds)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`
}

export default function MediaPicker({ open, type, media, mediaRoot, durations = {}, onPick, onClose }) {
  const t = useProductEditorTexts()
  const [search, setSearch] = useState("")
  const list = useMemo(() => (media || []).filter((m) => m.type === type && (!search || fileName(m.url).toLowerCase().includes(search.toLowerCase()))), [media, type, search])
  if (!open) return null
  return (
    <Modal isOpen onClose={onClose} variant="admin_compact" title={type === "image" ? t`Pick an image` : t`Pick an audio file`} styleOverrides={{ container: "bg-admin_paper w-full max-w-[860px] h-[80vh] rounded-md border border-admin_bordered shadow-lg flex flex-col overflow-hidden", contentWrapper: "flex-1 min-h-0 flex flex-col", content: "flex-1 min-h-0 flex flex-col p-0" }}>
      <div className="flex flex-col flex-1 min-h-0" data-testid="media-picker">
        <div className="p-3">
          <TextInput value={search} onChange={setSearch} placeholder={t`Search the media library`} autoFocus />
        </div>
        <div className="flex-1 overflow-y-auto px-3 pb-3">
          {!list.length && <p className="text-sm text-admin_text_secondary text-center py-8">{type === "image" ? t`No image files in the media library — upload one first` : t`No audio files in the media library — upload one first`}</p>}
          {type === "image" ? (
            <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(160px,1fr))]">
              {list.map((m) => (
                <button key={m.url} type="button" onClick={() => onPick(m)} className="flex flex-col gap-1 text-left group" data-testid="media-item">
                  <div className="w-full h-[110px] rounded-md overflow-hidden bg-admin_neutral_1 border border-admin_bordered group-hover:border-admin_primary">
                    <img src={urljoin(mediaRoot, m.url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <span className="text-xs truncate text-admin_text_secondary">{fileName(m.url)}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col divide-y divide-admin_bordered/60">
              {list.map((m) => (
                <div key={m.url} className="flex items-center gap-3 py-2" data-testid="media-item">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{fileName(m.url)}</div>
                    <AudioPlayer src={urljoin(mediaRoot, m.url)} compact inlineTime showRate={false} className="mt-1" />
                  </div>
                  <span className="text-xs text-admin_text_secondary w-12 text-right">{formatDuration(durations[m.url])}</span>
                  <Button variant="admin_secondary" className="h-[30px] text-xs px-3" onClick={() => onPick(m)}>{t`Select`}</Button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex justify-end p-3 border-t border-admin_bordered">
          <Button variant="admin_secondary" onClick={onClose}>{t`Cancel`}</Button>
        </div>
      </div>
    </Modal>
  )
}

export { fileName }
